import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, ArrowLeft, Image as ImageIcon, Eye, Download, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';

interface Category {
  id: string;
  name: string;
  products: Product[];
}

interface Product {
  id: string;
  name: string;
  sku: string;
  images: ImageFile[];
}

interface ImageFile {
  id: string;
  name: string;
  url: string;
  path: string;
  uploadedAt: string;
  size: number;
}

interface MyImagesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MyImagesModal: React.FC<MyImagesModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentView, setCurrentView] = useState<'categories' | 'products' | 'images'>('categories');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null);
  const [pathHistory, setPathHistory] = useState<string[]>([]);

  // Cargar categorías y productos
  const loadCategories = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      console.log('📁 Cargando categorías para usuario:', user.uid);
      
      // Usar el mismo servicio que el resto del sistema
      const optimizedService = (await import('../services/optimizedInventoryService')).default;
      const categoriesData = await optimizedService.getCategories();
      
      console.log(`📊 Categorías obtenidas del servicio: ${categoriesData.length}`);
      
      if (categoriesData.length === 0) {
        setError('No se encontraron categorías. Las categorías deben estar definidas en el sistema.');
        setLoading(false);
        return;
      }
      
      // Convertir las categorías del servicio al formato del modal
      // Mostrar todas las categorías (habilitadas y deshabilitadas)
      console.log(`📊 Mostrando todas las categorías: ${categoriesData.length}`);
      
      const categoriesForModal: Category[] = [];
      
      for (const category of categoriesData) {
        // Obtener productos de esta categoría desde Firestore
        const productsRef = window.auth.collection(window.auth.db, 'users', user.uid, 'categories', category.id, 'stock');
        const productsSnapshot = await window.auth.getDocs(productsRef);
        
        console.log(`📦 Productos en ${category.name}: ${productsSnapshot.docs.length}`);
        
        const products: Product[] = [];
        
        for (const productDoc of productsSnapshot.docs) {
          const productData = productDoc.data();
          const sku = productDoc.id;
          
          console.log(`📦 Procesando producto: ${sku} - ${productData.name || 'Sin nombre'}`);
          console.log(`📦 Datos del producto:`, productData);
          
          // Obtener imágenes de este producto desde Storage
          const images = await loadProductImages(category.id, sku);
          console.log(`🖼️ Imágenes encontradas para ${sku}: ${images.length}`);
          
          products.push({
            id: sku,
            name: productData.name || `Producto ${sku}`,
            sku: sku,
            images: images
          });
        }
        
        categoriesForModal.push({
          id: category.id,
          name: category.name,
          products: products
        });
      }
      
      setCategories(categoriesForModal);
      console.log(`✅ Cargadas ${categoriesForModal.length} categorías`);
      console.log(`📋 Resumen de categorías cargadas:`, categoriesForModal.map(cat => ({
        id: cat.id,
        name: cat.name,
        productsCount: cat.products.length,
        products: cat.products.map(p => ({ sku: p.sku, name: p.name, imagesCount: p.images.length }))
      })));
      
    } catch (error) {
      console.error('❌ Error cargando categorías:', error);
      setError('Error al cargar las categorías desde el servicio');
    } finally {
      setLoading(false);
    }
  };

  // Cargar imágenes de un producto específico
  const loadProductImages = async (categoryId: string, sku: string): Promise<ImageFile[]> => {
    if (!user || !window.auth?.storage) return [];

    try {
      console.log(`🖼️ Cargando imágenes para ${categoryId}/${sku}`);
      console.log(`🔧 Firebase Storage disponible:`, !!window.auth.storage);
      console.log(`🔧 Funciones disponibles:`, {
        ref: !!window.auth.ref,
        listAll: !!window.auth.listAll,
        getDownloadURL: !!window.auth.getDownloadURL,
        getMetadata: !!window.auth.getMetadata
      });
      
      // Intentar ambas rutas posibles
      const possiblePaths = [
        `users/${user.uid}/categories/${categoryId}/stock/${sku}`,
        `users/${user.uid}/products/${categoryId}/${sku}/images`,
        `users/${user.uid}/categories/${categoryId}/stock/${sku}/images`,
        `users/${user.uid}/tienda-ropa/${sku}`,
        `users/${user.uid}/tienda-ropa/${sku.toLowerCase()}`,
        `users/${user.uid}/clothing/${sku}`,
        `users/${user.uid}/clothing/${sku.toLowerCase()}`
      ];
      
      for (const imagesPath of possiblePaths) {
        try {
          console.log(`🔍 Intentando ruta: ${imagesPath}`);
          const imagesRef = window.auth.ref(window.auth.storage, imagesPath);
          console.log(`📁 Referencia creada:`, imagesRef);
          
          const imagesSnapshot = await window.auth.listAll(imagesRef);
          console.log(`📁 Resultado de listAll:`, imagesSnapshot);
          
          console.log(`📁 Archivos encontrados en ${imagesPath}: ${imagesSnapshot.items.length}`);
          console.log(`📁 Lista de archivos:`, imagesSnapshot.items.map((item: any) => item.name));
          
          if (imagesSnapshot.items.length > 0) {
            const images: ImageFile[] = [];
            
            for (const itemRef of imagesSnapshot.items) {
              const fileName = itemRef.name.toLowerCase();
              console.log(`🔍 Procesando archivo: ${itemRef.name} (${fileName})`);
              
              // Los archivos pueden no tener extensión, así que procesamos todos los archivos
              // y verificamos el tipo MIME después
              try {
                console.log(`✅ Procesando archivo: ${itemRef.name}`);
                const url = await window.auth.getDownloadURL(itemRef);
                const metadata = await window.auth.getMetadata(itemRef);
                
                console.log(`🔗 URL obtenida para ${itemRef.name}: ${url}`);
                console.log(`📄 Metadatos completos:`, metadata);
                console.log(`📄 Content Type:`, metadata.contentType);
                
                // Verificar si es una imagen por el tipo MIME o por extensión
                const contentType = metadata.contentType || '';
                console.log(`🔍 Verificando content type: "${contentType}" para archivo: ${itemRef.name}`);
                
                // Detectar si es una imagen por content type o por extensión
                const fileName = itemRef.name.toLowerCase();
                const hasImageExtension = fileName.endsWith('.jpg') || 
                                         fileName.endsWith('.jpeg') || 
                                         fileName.endsWith('.png') || 
                                         fileName.endsWith('.gif') || 
                                         fileName.endsWith('.webp');
                
                const isImage = (contentType && contentType.startsWith('image/')) || hasImageExtension;
                console.log(`🔍 Es imagen: ${isImage} (contentType: ${contentType}, extensión: ${hasImageExtension})`);
                
                // Si el content type indica que es una imagen, procesar
                if (isImage) {
                  // Filtrar solo imágenes subidas manualmente (frontal, reverso, closeups)
                  // Excluir imágenes generadas con IA
                  const isAIGenerated = fileName.includes('ai') || 
                                       fileName.includes('generated') || 
                                       fileName.includes('modelo') ||
                                       fileName.includes('ia');
                  
                  if (!isAIGenerated) {
                    images.push({
                      id: itemRef.name,
                      name: itemRef.name,
                      url: url,
                      path: itemRef.fullPath,
                      uploadedAt: metadata.timeCreated || new Date().toISOString(),
                      size: metadata.size || 0
                    });
                    console.log(`✅ Imagen subida agregada: ${itemRef.name}`);
                  } else {
                    console.log(`❌ Imagen IA excluida: ${itemRef.name}`);
                  }
                } else {
                  console.log(`❌ No es imagen (tipo: ${contentType}): ${itemRef.name}`);
                }
              } catch (err) {
                console.warn('⚠️ Error obteniendo URL para archivo:', itemRef.name, err);
              }
            }
            
            console.log(`✅ ${images.length} imágenes cargadas desde ${imagesPath}`);
            return images;
          }
        } catch (error) {
          console.log(`⚠️ Error en ruta ${imagesPath}:`, error instanceof Error ? error.message : error);
          console.log(`⚠️ Tipo de error:`, typeof error);
          console.log(`⚠️ Stack trace:`, error instanceof Error ? error.stack : 'No stack trace');
        }
      }
      
      console.log(`❌ No se encontraron imágenes para ${categoryId}/${sku} en ninguna ruta`);
      return [];
      
    } catch (error) {
      console.error('❌ Error cargando imágenes:', error);
      return [];
    }
  };

  // Filtrar categorías por término de búsqueda
  const filteredCategories = categories.filter(category => {
    if (!searchTerm) return true;
    
    const categoryMatch = category.name.toLowerCase().includes(searchTerm.toLowerCase());
    const productMatch = category.products.some(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return categoryMatch || productMatch;
  });

  // Navegar a productos de una categoría
  const navigateToProducts = (category: Category) => {
    setSelectedCategory(category);
    setCurrentView('products');
    setPathHistory([...pathHistory, 'categories']);
  };

  // Navegar a imágenes de un producto
  const navigateToImages = (product: Product) => {
    setSelectedProduct(product);
    setCurrentView('images');
    setPathHistory([...pathHistory, 'products']);
  };

  // Volver atrás
  const goBack = () => {
    if (pathHistory.length === 0) {
      setCurrentView('categories');
      setSelectedCategory(null);
      setSelectedProduct(null);
    } else if (currentView === 'images') {
      setCurrentView('products');
      setSelectedProduct(null);
      const newHistory = [...pathHistory];
      newHistory.pop();
      setPathHistory(newHistory);
    } else if (currentView === 'products') {
      setCurrentView('categories');
      setSelectedCategory(null);
      const newHistory = [...pathHistory];
      newHistory.pop();
      setPathHistory(newHistory);
    }
  };

  // Abrir imagen en vista ampliada
  const openImagePreview = (image: ImageFile) => {
    setSelectedImage(image);
  };

  // Descargar imagen
  const downloadImage = async (image: ImageFile) => {
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = image.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('❌ Error descargando imagen:', error);
    }
  };

  // Eliminar imagen
  const deleteImage = async (image: ImageFile) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta imagen?')) return;

    try {
      const imageRef = window.auth.storage.ref(image.path);
      await window.auth.storage.deleteObject(imageRef);
      
      // Recargar datos
      await loadCategories();
      
      // Si estamos viendo imágenes de un producto, recargar esas imágenes específicamente
      if (selectedProduct) {
        const updatedImages = await loadProductImages(selectedCategory!.id, selectedProduct.sku);
        setSelectedProduct({
          ...selectedProduct,
          images: updatedImages
        });
      }
      
    } catch (error) {
      console.error('❌ Error eliminando imagen:', error);
      alert('Error al eliminar la imagen');
    }
  };

  // Cargar datos al abrir el modal
  useEffect(() => {
    if (isOpen) {
      loadCategories();
      setCurrentView('categories');
      setSelectedCategory(null);
      setSelectedProduct(null);
      setSelectedImage(null);
      setPathHistory([]);
      setSearchTerm('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-4">
              {currentView !== 'categories' && (
                <button
                  onClick={goBack}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Mis Imágenes
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {currentView === 'categories' && 'Selecciona una categoría'}
                  {currentView === 'products' && selectedCategory && `Productos en ${selectedCategory.name}`}
                  {currentView === 'images' && selectedProduct && `Imágenes de ${selectedProduct.name}`}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nombre de producto o SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {loading && (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-300">Cargando...</span>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-red-500 text-lg mb-2">❌</div>
                  <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
                  <div className="space-y-2">
                    <button
                      onClick={loadCategories}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors mr-2"
                    >
                      Reintentar
                    </button>
                    <button
                      onClick={() => {
                        console.log('💡 Las categorías deben estar definidas en el sistema');
                        alert('Las categorías deben estar definidas en el sistema. Contacta al administrador si no aparecen.');
                      }}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      Información
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!loading && !error && (
              <div className="h-full overflow-y-auto p-6">
                {currentView === 'categories' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredCategories.map((category) => (
                      <motion.div
                        key={category.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => navigateToProducts(category)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-blue-500" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 dark:text-gray-100">
                              {category.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {category.products.length} productos
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {currentView === 'products' && selectedCategory && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedCategory.products.map((product) => (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => navigateToImages(product)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                            <span className="text-green-500 text-lg">📦</span>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 dark:text-gray-100">
                              {product.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              SKU: {product.sku}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {product.images.length} imágenes
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {currentView === 'images' && selectedProduct && (
                  <div>
                    {selectedProduct.images.length === 0 ? (
                      <div className="text-center py-12">
                        <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                          No hay imágenes cargadas para este producto
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          Sube imágenes para este producto desde la gestión de inventario
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {selectedProduct.images.map((image) => (
                          <motion.div
                            key={image.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="relative group bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden"
                          >
                            <div className="aspect-square relative">
                              <img
                                src={image.url}
                                alt={image.name}
                                className="w-full h-full object-cover"
                                onClick={() => openImagePreview(image)}
                              />
                              
                              {/* Overlay de acciones */}
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openImagePreview(image);
                                    }}
                                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                  >
                                    <Eye className="w-4 h-4 text-gray-700" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadImage(image);
                                    }}
                                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                  >
                                    <Download className="w-4 h-4 text-gray-700" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteImage(image);
                                    }}
                                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </button>
                                </div>
                              </div>
                            </div>
                            
                            <div className="p-3">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {image.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(image.uploadedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Modal de vista ampliada de imagen */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-60 p-4"
              onClick={() => setSelectedImage(null)}
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                className="relative max-w-4xl max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={selectedImage.url}
                  alt={selectedImage.name}
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default MyImagesModal;